import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {environment} from '../../environments/environment';
import {LoginDto} from '../../core/dtos/login.dto';
import {ForgotPasswordRequestDto} from '../../core/dtos/forgot-password-request.dto';

@Injectable({providedIn: 'root'})
export class AuthService {
  private accessTokenKey = 'access_token';
  private emailKey = 'email';
  private loggedIn$ = new BehaviorSubject<boolean>(this.isLoggedIn());
  private refreshRequest$: Observable<boolean> | null = null;

  constructor(private http: HttpClient) {
  }

  login(email: string, password: string): Observable<LoginDto> {
    return this.http.post<LoginDto>(`${environment.apiUrl}/api/auth/login`, {email, password}, { withCredentials: true })
      .pipe(
        tap(res => {
            localStorage.setItem(this.accessTokenKey, res.accessToken);
            localStorage.setItem(this.emailKey, res.email);
            this.loggedIn$.next(true);
          }
        ));
  }

  forgotPassword(email: string): Observable<void> {
    const request: ForgotPasswordRequestDto = {email};
    return this.http.post<void>(`${environment.apiUrl}/api/account/forgot-password`, request);
  }

  refresh(): Observable<boolean> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refreshRequest$ = this.http
      .post<LoginDto>(
        `${environment.apiUrl}/api/auth/refresh`,
        {},
        {withCredentials: true}
      )
      .pipe(
        tap(res => {
          if (!res?.accessToken) {
            throw new Error('Invalid refresh response');
          }

          localStorage.setItem(this.accessTokenKey, res.accessToken);
          localStorage.setItem(this.emailKey, res.email);
          this.loggedIn$.next(true);
        }),
        map(() => true),
        catchError(() => this.logout().pipe(map(() => false))),
        finalize(() => {
          // Only clear the request that just completed. This prevents an older
          // finalizer from clearing a newer refresh request in a re-entrant flow.
          if (this.refreshRequest$ === refreshRequest$) {
            this.refreshRequest$ = null;
          }
        }),
        shareReplay({bufferSize: 1, refCount: false})
      );

    this.refreshRequest$ = refreshRequest$;
    return refreshRequest$;
  }

  logout(): Observable<void> {
    this.clearLocalSession();

    return this.http.post<void>(
      `${environment.apiUrl}/api/auth/logout`,
      {},
      {withCredentials: true}
    ).pipe(
      // Local logout must still succeed when the API is unavailable. The
      // server request remains the best effort that revokes the HttpOnly
      // refresh cookie whenever it can be reached.
      catchError(() => of(undefined))
    );
  }

  private clearLocalSession(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.emailKey);
    this.loggedIn$.next(false);
  }

  get token(): string | null {
    return localStorage.getItem(this.accessTokenKey);
  }

  get loggedInEmail(): string | null {
    return localStorage.getItem(this.emailKey);
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.loggedIn$.asObservable();
  }

  isLoggedIn(): boolean {
    const token = localStorage.getItem(this.accessTokenKey);
    return !!token;
  }
}
